import React, {useRef, useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Animated, Dimensions, StyleSheet} from 'react-native';
import {FlatList} from 'react-native-gesture-handler';
import {OnBoardingListItem} from '../../../interfaces/components/onboarding';
import {Theme} from '../../../theme/lightTheme';
import MainLayout from '../../mainLayout';
import {tr} from '../../../translations';
import {ExpandingDot} from 'react-native-animated-pagination-dots';
import {onboardingList} from '../../../assets/static/onboarding';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import GenericButton from '../../../components/button';
import Icon from '../../../components/icon';
import {hideOnboardingScreen} from './actions';
import OnboardingItem from '../../../components/onboardingItem';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';

const Container = styled.View<{topInset: number; height: number; isSmallScreen: boolean; isSmallLandscape: boolean}>`
  flex: 1;
  width: 100%;
  margin-top: ${({topInset, height, isSmallScreen, isSmallLandscape}) => 
    isSmallLandscape ? Math.max(topInset, height * 0.2) : 
    isSmallScreen ? Math.max(topInset, height * 0.5) : Math.max(topInset, height)}px;
`;
const BottomContainer = styled.View`
  align-items: center;
  position: absolute;
`;
const TopIconContainer = styled.View<{width: number}>`
  position: absolute;
  top: 0;
  align-items: center;
  justify-content: center;
  width: ${({width}) => width}px;
  align-self: center;
  pointer-events: none;
`;
const IndicatorContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;
const Row = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const OnBoarding = (): React.ReactElement => {
  const theme = useTheme() as Theme;
  const flatListRef = useRef<FlatList>(null);
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const scrollX = useRef(new Animated.Value(0)).current;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const {width, hdp, wdp, getByScreenSize} = useResponsiveDimensions();
  
  // Check if device is small (800px width and 500-700px height)
  const isSmallScreen = width <= 800;
  const screenHeight = Dimensions.get('window').height;
  const isSmallHeight = screenHeight >= 500 && screenHeight <= 700;
  const isLandscape = width > screenHeight;
  const isSmallLandscape = isSmallScreen && isLandscape;

  const handleScroll = (event: any) => {
    const {contentOffset} = event.nativeEvent;
    const screenWidth = Dimensions.get('window').width;
    scrollX.setValue(contentOffset.x);
    const calculatedIndex = Math.round(contentOffset.x / screenWidth);
    setSelectedIndex(calculatedIndex);
  };

  const onNextPressHandler = async () => {
    if (!flatListRef?.current) {
      return;
    }
    if (selectedIndex < onboardingList.length - 1) {
      flatListRef?.current.scrollToOffset({
        offset: (selectedIndex + 1) * wdp(100),
        animated: true,
      });
    } else {
      hideOnboardingScreen();
    }
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: OnBoardingListItem;
    index: number;
  }): React.ReactElement => {
    return (
      <OnboardingItem key={index} index={index} item={item} scrollX={scrollX} />
    );
  };

  const renderDots = (): React.ReactElement => {
    return (
      <IndicatorContainer style={{width, height: hdp(5)}}>
        {/*@ts-ignore*/}
        <ExpandingDot
          data={onboardingList}
          scrollX={scrollX}
          expandingDotWidth={24}
          activeDotColor={theme.onboarding.text}
          inActiveDotColor={theme.onboarding.text}
          inActiveDotOpacity={0.4}
          dotStyle={{
            backgroundColor: 'transparent',
            width: 8,
            height: 8,
            borderRadius: 4,
            marginHorizontal: 5,
          }}
          containerStyle={{}}
        />
      </IndicatorContainer>
    );
  };

  const buttonStyles = StyleSheet.create({
    buttonContainer: {
      marginBottom: isSmallLandscape ? 5 : isSmallScreen ? 8 : 15,
      width: isSmallLandscape ? '45%' : isSmallScreen ? '85%' : width < 900 ? '40%' : '30%',
      minWidth: isSmallLandscape ? 150 : isSmallScreen ? 180 : 220,
    },
    buttonContainerWide: {
      marginBottom: isSmallLandscape ? 5 : isSmallScreen ? 8 : 15,
      width: isSmallLandscape ? '45%' : isSmallScreen ? '85%' : width < 900 ? '40%' : '30%',
      minWidth: isSmallLandscape ? 150 : isSmallScreen ? 180 : 220,
      marginRight: isSmallLandscape ? '5%' : width >= 900 ? '5%' : undefined,
      backgroundColor: theme.onboarding.text,
      borderRadius: 100,
    },
  });

  const signInButton = (
    <GenericButton
      outline
      style={buttonStyles.buttonContainerWide}
      // dynamicWidth={width >= 900}
      radius={100}
      label={tr('onboarding.signIn')}
      labelStyle={
        width >= 900
          ? {
              minWidth: width * 0.05,
            }
          : undefined
      }
      onPress={hideOnboardingScreen}
      showRightIcon
      rightIconName={'navArrow'}
      rightIconSize={getByScreenSize(45, 55) - 10}
      rightIconColor={theme.primary}
      rightIconStrokeColor={theme.onboarding.text}
      rightIconContainerStyle={{
        right: 5,
      }}
    />
  );

  const nextButton = (
    <GenericButton
      dynamicWidth={width >= 900}
      style={buttonStyles.buttonContainer}
      radius={100}
      label={tr('onboarding.next')}
      labelStyle={
        width >= 900
          ? {
              paddingHorizontal: width * 0.05,
            }
          : undefined
      }
      onPress={onNextPressHandler}
      showRightIcon
      rightIconName={'navArrow'}
      rightIconSize={getByScreenSize(45, 55) - 10}
      rightIconColor={theme.primary}
      rightIconStrokeColor={theme.onboarding.text}
      rightIconContainerStyle={{
        right: 5,
        transform: [{rotate: '43deg'}],
      }}
    />
  );

  const renderButtons = () => {
    if (width >= 900 || isSmallLandscape) {
      return (
        <Row>
          {signInButton}
          {nextButton}
        </Row>
      );
    }

    return (
      <Row style={{flexDirection: 'column', alignItems: 'center'}}>
        {signInButton}
        {nextButton}
      </Row>
    );
  };

  return (
    <MainLayout
      backgroundColor={theme.onboarding.background}
      showOverlay={false}
      noPadding
      noPaddingHeader
      enableScroll={false}>
      <Container topInset={topInset || 0} height={hdp(5)} isSmallScreen={isSmallScreen} isSmallLandscape={isSmallLandscape}>
        <TopIconContainer width={width}>
          <Icon
            name={'onboardingLogo'}
            size={isSmallLandscape ? width * 0.08 : isSmallScreen ? width * 0.15 : width * getByScreenSize(0.35, 0.08, 0.35)}
            height={isSmallLandscape ? width * 0.08 * 0.3 : isSmallScreen ? width * 0.15 * 0.3 : width * getByScreenSize(0.35, 0.08, 0.35) * 0.3}
          />
        </TopIconContainer>
        <FlatList
          ref={flatListRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          pagingEnabled
          data={onboardingList}
          renderItem={renderItem}
          initialNumToRender={1}
          maxToRenderPerBatch={2}
          removeClippedSubviews
          windowSize={2}
          scrollEventThrottle={16}
          onMomentumScrollEnd={() => {}}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 50,
          }}
          style={{width}}
          onScroll={handleScroll}
          onScrollToIndexFailed={info => {
          }}
        />
        <BottomContainer
          style={{
            bottom: isSmallLandscape ? Math.max(hdp(2), bottomInset || 0) : isSmallScreen ? Math.max(hdp(3), bottomInset || 0) : hdp(5) > (bottomInset || 0) ? hdp(5) : bottomInset,
            width,
          }}>
          {renderDots()}
          {renderButtons()}
        </BottomContainer>
      </Container>
    </MainLayout>
  );
};

export default OnBoarding;
